import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { jwtDecode } from "jwt-decode";

import { api, getOnboardingAccessToken } from "@utils/api";
import { hasPageAccess, PermissionGrants } from "@utils/permissions";


interface AuthContextType {
  permissions: PermissionGrants;
  permissionsLoading: boolean;
  can: (permission: string) => boolean;
  canAccessPath: (path: string) => boolean;
  refreshPermissions: () => Promise<void>;
  token: string;
  setToken: (token: string) => void;
  id: string;
  setId: (id: string) => void;
  fullName: string;
  setFullName: (fullName: string) => void;
  username: string;
  setUsername: (username: string) => void;
  roles: string[];
  setRoles: (roles: string[]) => void;
  email: string;
  setEmail: (email: string) => void;
  onboardingStatus: string;
  setOnboardingStatus: (status: string) => void;
  hasRequestedTokenRefresh: boolean;
  setHasRequestedTokenRefresh: (hasRequestedTokenRefresh: boolean) => void;
  resetAuthStates: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { pathname, search: queryParams } = useLocation();

  const [token, setToken] = useState(""); // Use to check if user is signed in
  const [id, setId] = useState("");
  const [fullName, setFullName] = useState("");
  const [username, setUsername] = useState("");
  const [roles, setRoles] = useState<string[] | []>([]);
  const [permissions, setPermissions] = useState<PermissionGrants>({});
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const permissionSnapshot = useRef("");
  const permissionGeneration = useRef(0);
  const refreshPermissions = useCallback(async () => {
    const generation = ++permissionGeneration.current;
    if (!token) { setPermissions({}); setPermissionsLoading(false); return; }
    try {
      const { data } = await api.get("access/me", { headers: { Authorization: `Bearer ${token}` } });
      if (generation !== permissionGeneration.current) return;
      const snapshot = JSON.stringify(data.permissions);
      if (permissionSnapshot.current && permissionSnapshot.current !== snapshot) {
        await queryClient.cancelQueries();
        queryClient.removeQueries({ predicate: query => query.queryKey[0] !== "access" });
      }
      permissionSnapshot.current = snapshot;
      setPermissions(data.permissions);
      setRoles(data.roles);
    } catch {
      if (generation === permissionGeneration.current) {
        setPermissions({});
        queryClient.removeQueries({ predicate: query => query.queryKey[0] !== "access" });
      }
    } finally {
      if (generation === permissionGeneration.current) setPermissionsLoading(false);
    }
  }, [token, queryClient]);

  useEffect(() => {
    setPermissions({});
    setPermissionsLoading(Boolean(token));
    void refreshPermissions();
    const refresh = () => { if (document.visibilityState === "visible") void refreshPermissions(); };
    const timer = window.setInterval(refresh, 30000);
    window.addEventListener("focus", refresh);
    window.addEventListener("permissions-changed", refresh);
    const interceptor = api.interceptors.response.use(response => response, error => {
      if (error.response?.status === 403 && !error.config?.url?.includes("access/me")) refresh();
      return Promise.reject(error);
    });
    return () => {
      permissionGeneration.current++;
      window.clearInterval(timer);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("permissions-changed", refresh);
      api.interceptors.response.eject(interceptor);
    };
  }, [token, refreshPermissions]);
  const [email, setEmail] = useState("");
  const [onboardingStatus, setOnboardingStatus] = useState("");
  const [hasRequestedTokenRefresh, setHasRequestedTokenRefresh] =
    useState(false);

  const resetAuthStates = () => {
    permissionGeneration.current++;
    setPermissions({});
    permissionSnapshot.current = "";
    setToken("");
    setId("");
    setFullName("");
    setUsername("");
    setRoles([]);
    setEmail("");
    setOnboardingStatus("");
    setHasRequestedTokenRefresh(false);

    queryClient.clear();
  };

  // useEffect(() => {
  //   console.log("token details:", {
  //     id,
  //     fullName,
  //     username,
  //     roles,
  //     email,
  //     hasRequestedTokenRefresh,
  //   });
  // }, [id]);

  // Load token from sessionStorage on mount
  useLayoutEffect(() => {
    const storedToken = sessionStorage.getItem("access_token");
    if (storedToken) {
      try {
        const decoded: any = jwtDecode(storedToken);
        // Check if token is expired
        const isExpired = decoded.exp * 1000 < Date.now();

        if (!isExpired) {
          setToken(storedToken);
          setId(decoded.id);
          setFullName(decoded.full_name);
          setUsername(decoded.username);
          setEmail(decoded.email);
          setRoles(decoded.roles);
          setOnboardingStatus(decoded.onboarding_status || "approved");
          import.meta.env.DEV && console.log("[DEV] Token loaded from sessionStorage");
        } else {
          // Token expired, clear it
          sessionStorage.clear();
          import.meta.env.DEV && console.log("[DEV] Token expired, cleared sessionStorage");
        }
      } catch (error) {
        // Invalid token, clear it
        sessionStorage.clear();
        console.error("[Auth] Invalid token in sessionStorage:", error);
      }
    }
    setHasRequestedTokenRefresh(true);
  }, []);

  useLayoutEffect(() => {
    import.meta.env.DEV && console.log("[DEV] Token in auth provider:", token);

    // Persist token to sessionStorage
    if (token) {
      sessionStorage.setItem("access_token", token);
    } else {
      sessionStorage.removeItem("access_token");
    }

    const authInterceptor = api.interceptors.request.use(config => {
      // @ts-expect-error _retry does exist, we set it in the next useLayoutEffect
      if (!config?._retry && token) {
        config.headers.Authorization = `Bearer ${token}`;
        return config;
      }

      const onboardingToken = getOnboardingAccessToken();
      const isOnboardingRequest = typeof config.url === "string" && config.url.startsWith("onboarding/");
      if (isOnboardingRequest && onboardingToken) {
        config.headers.Authorization = `Bearer ${onboardingToken}`;
      }
      return config;
    });

    return () => {
      api.interceptors.request.eject(authInterceptor);
    };
  }, [token]);

  /*
   * Single Refresh Call: The isRefreshing lock ensures that only one request triggers the token refresh.
   * Queued Requests: Subsequent requests wait for the token refresh to complete by subscribing to the refreshSubscribers.
   * Retry Requests: Once the token is refreshed, all queued requests are retried with the new token.
   */
  const isRefreshing = useRef(false);
  const refreshSubscribers = useRef<((token: string) => void)[]>([]);

  const subscribeTokenRefresh = (callback: (token: string) => void) => {
    refreshSubscribers.current.push(callback);
  };

  const onTokenRefreshed = (newToken: string) => {
    refreshSubscribers.current.forEach(callback => callback(newToken));
    refreshSubscribers.current = [];
  };

  useLayoutEffect(() => {
    const refreshInterceptor = api.interceptors.response.use(
      response => response, // Pass through for successful responses
      async error => {
        if (
          error?.response?.status === 401 &&
          (error?.response?.data?.detail === "Not authenticated" ||
            error?.response?.data?.detail === "Token Has expired")
        ) {
          if (!isRefreshing.current) {
            isRefreshing.current = true; // Lock refreshing

            try {
              const res = await api.get("token/refresh");
              if (!res.data) {
                throw new Error("No new access token");
              }
              const token = res.data.access_token;
              const decoded: any = jwtDecode(token);
              setToken(token);
              setId(decoded.id);
              setFullName(decoded.full_name);
              setUsername(decoded.username);
              setEmail(decoded.email);
              setRoles(decoded.roles);
              setOnboardingStatus(decoded.onboarding_status || "approved");

              // Notify all subscribers
              onTokenRefreshed(token);

              // Mark refreshing as complete
              isRefreshing.current = false;

              // Retry the original request with the new token
              error.config.headers.Authorization = `Bearer ${token}`;
              return api.request(error.config);
            } catch (refreshError) {
              console.error("Token refresh failed:", refreshError);
              setToken("");
              sessionStorage.clear();
              navigate("/", { state: { redirectURL: pathname + queryParams } });
              isRefreshing.current = false;
              return Promise.reject(refreshError);
            }
          }

          // Wait for the token to refresh
          return new Promise(resolve => {
            subscribeTokenRefresh(token => {
              error.config.headers.Authorization = `Bearer ${token}`;
              resolve(api.request(error.config)); // Called by onTokenRefreshed
            });
          });
        }

        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.response.eject(refreshInterceptor);
    };
  }, [pathname, queryParams]);

  return (
    <AuthContext.Provider
      value={{
        permissions,
        permissionsLoading,
        can: permission => Boolean(permissions[permission]),
        canAccessPath: path => hasPageAccess(permissions, path),
        refreshPermissions,
        token,
        setToken,
        id,
        setId,
        username,
        setUsername,
        fullName,
        setFullName,
        roles,
        setRoles,
        email,
        setEmail,
        onboardingStatus,
        setOnboardingStatus,
        hasRequestedTokenRefresh,
        setHasRequestedTokenRefresh,
        resetAuthStates,
      }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
